<template>
  <Layout
    class="onboarding-loading16167"
    :title="currentTitle"
    :titleTransition="true"
  >
    <template #aboveTitle>
      <Rive
        class="onboarding-loading16167__rive"
        :src="getImage(page.animation.src)"
        :width="page.animation.width"
        :height="page.animation.height"
        :animations="currentAnimation"
        fit="FitWidth"
        alignment="BottomCenter"
      />
    </template>
  </Layout>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'

import { getImage, QuestionnaireView } from '@/models/onboarding'
import { Loading16167Page } from '@/models/onboarding.types'
import { PublicInterface } from '@/models/interfaces'

import Rive from '@/app/components/Rive.vue'
import Layout from '../Layout.vue'
import { waitTimer } from '@/helpers/timer'
import { storage } from '@/helpers/storage'

const props = defineProps<{
  view: PublicInterface<QuestionnaireView>
}>()

const page = computed(() => {
  return props.view.currentPage as Loading16167Page
})

const currentState = ref<'loading' | 'done' | 'error'>('loading')

const currentAnimation = computed(() => {
  return currentState.value === 'loading' ? 'loading' : 'loaded'
})

const currentTitle = computed(() => {
  switch (currentState.value) {
    case 'loading':
      return page.value.title
    case 'done':
      return page.value.titleDone
    case 'error':
      return page.value.titleError
    default:
      return page.value.titleError
  }
})

onMounted(async () => {
  try {
    // Create the reading list from the choices from the previous page,
    // and wait at least 3 second.
    const [response] = await Promise.allSettled([
      props.view.createReadingList16167(),
      waitTimer(3000),
    ])
    if (response.status === 'rejected') {
      throw response.reason
    }

    currentState.value = 'done'
  } catch (error) {
    currentState.value = 'error'
    // Remove the book covers from local storage
    // to prevent the reading list widget from being
    // shown in the Discover
    storage.removeItem('sf_lq_books16167')
  }

  props.view.showContinueButton()
})
</script>

<style lang="scss" scoped>
.onboarding-loading16167 {
  gap: 24px;
  padding-top: 0;
  // Extra space at the bottom to accomodate the footer
  // which is absolute positioned.
  padding-bottom: calc(var(--onboarding-padding) * 4);
  @media (min-height: 650px) {
    justify-content: center;
  }

  &:deep(.onboarding-layout__above-title) {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  &:deep(.onboarding-layout__text) {
    // Prevent layout shifts when the text updates.
    min-height: 80px;
  }

  &:deep(.onboarding-layout__above-title) {
    min-height: 0;
  }

  &__rive {
    max-width: 285px;
  }

  &:deep(~ .onboarding-questionnaire__button-wrapper) {
    position: absolute;
    bottom: 0;
  }
}
</style>
